import { create, type StateCreator } from "zustand";
import { type DevtoolsOptions, devtools } from "zustand/middleware";
import type { ShareVoiceSidebarCategory } from "@/data-access/share-of-voice/queries/shareOfVoice-sidebar-filter-queries";
import { DEFAULT_FILTER_DAYS } from "@/store/url-params/urlParamsCodec";
import { createNewsFeedParamsActions } from "./newsFeed-params.actions";

export type SelectedFilterType = Map<ShareVoiceSidebarCategory, string[]>;

/** Graph period for news feed volume chart (day / week / month only). */
export type NewsFeedPeriod = "day" | "week" | "month" | null;

export type DateRangeParams = {
  filterDays: number | undefined;
  isCustomDaysRange: boolean;
  startDate?: Date;
  endDate?: Date;
  period: NewsFeedPeriod;
};

export type FilterSelectionParams = {
  selectedFilters: SelectedFilterType;
};

export type NewsFeedParamsActions = {
  setPeriod: (period: NewsFeedPeriod) => void;
  setFilterDays: (days: number) => void;
  setCustomDateRange: (startDate: Date, endDate: Date) => void;
  clearState: () => void;
};

export type FilterSelectionActions = {
  toggleFilterSelection: (
    selection: ShareVoiceSidebarCategory,
    item: string | number,
  ) => void;
  clearAllSelectedFilters: () => void;
  clearSelectedFiltersByCategory: (category: ShareVoiceSidebarCategory) => void;
  clearDynamicFilters: () => void;
  removeFilterItemsByCategory: (
    category: ShareVoiceSidebarCategory,
    itemsToRemove: string[],
  ) => void;
};

export { DEFAULT_FILTER_DAYS };

export type NewsFeedParamsSlice = DateRangeParams &
  FilterSelectionParams &
  NewsFeedParamsActions &
  FilterSelectionActions;

const createNewsFeedParamsSlice: StateCreator<
  NewsFeedParamsSlice,
  [["zustand/devtools", never]]
> = (set, get, api) => ({
  filterDays: DEFAULT_FILTER_DAYS,
  isCustomDaysRange: false,
  startDate: undefined,
  endDate: undefined,
  period: "day",
  selectedFilters: new Map([["CATEGORY", ["NEWS"]]]) as SelectedFilterType,
  ...createNewsFeedParamsActions(set, get, api),
});

const createNewsFeedParamsSliceWithDefaults = (
  ...a: Parameters<typeof createNewsFeedParamsSlice>
) => ({
  ...createNewsFeedParamsSlice(...a),
});

const useNewsFeedParamsStore = create<NewsFeedParamsSlice>()(
  devtools(createNewsFeedParamsSliceWithDefaults, {
    name: "news-feed-params-store",
    serialize: {
      options: true,
    },
  } as unknown as DevtoolsOptions),
);

export default useNewsFeedParamsStore;
